import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import {
  Users, Mail, CreditCard, TrendingUp, ArrowLeft, Clock, Send,
  AlertCircle, Eye, CalendarClock, Pause, Play, Zap, Trash2, X,
  FileText, Image, Video, ChevronDown, CalendarDays, Shield, Lock, Gift
} from "lucide-react";
import { useTranslation } from "../i18n/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_ICONS = {
  sent: Send, paid: Clock, scheduled: Shield, pending_payment: CreditCard,
  paused: Pause, draft: FileText, cancelled: X, reserved: Clock, failed: AlertCircle, delivery_failed: AlertCircle,
};

const STATUS_COLORS = {
  sent: "bg-emerald-100 text-emerald-700 border-emerald-200",
  paid: "bg-blue-100 text-blue-700 border-blue-200",
  scheduled: "bg-emerald-100 text-emerald-700 border-emerald-200",
  pending_payment: "bg-amber-100 text-amber-700 border-amber-200",
  paused: "bg-orange-100 text-orange-700 border-orange-200",
  draft: "bg-gray-100 text-gray-600 border-gray-200",
  cancelled: "bg-red-100 text-red-700 border-red-200",
  reserved: "bg-violet-100 text-violet-700 border-violet-200",
  failed: "bg-red-100 text-red-700 border-red-200",
  delivery_failed: "bg-red-100 text-red-700 border-red-200",
};

const FILTER_VALUES = ["all", "scheduled", "reserved", "paid", "sent", "pending_payment", "paused", "delivery_failed"];

function StatusBadge({ status }) {
  const { t } = useTranslation();
  const Icon = STATUS_ICONS[status] || FileText;
  const color = STATUS_COLORS[status] || "bg-gray-100 text-gray-600 border-gray-200";
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${color}`} data-testid={`status-badge-${status}`}>
      <Icon className="w-3 h-3" />
      {t(`status.${status}`) || status}
    </span>
  );
}

function StatCard({ icon: Icon, title, value, subtitle, color }) {
  return (
    <Card data-testid={`stat-card-${title.toLowerCase().replace(/\s/g, "-")}`}>
      <CardContent className="p-5">
        <div className="flex items-center gap-4">
          <div className={`p-3 rounded-xl ${color}`}><Icon className="w-5 h-5 text-white" /></div>
          <div>
            <p className="text-sm text-stone-500 font-medium">{title}</p>
            <p className="text-2xl font-bold text-stone-900">{value}</p>
            {subtitle && <p className="text-xs text-stone-400 mt-0.5">{subtitle}</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function MessageDetailModal({ message, onClose, onAction, t }) {
  if (!message) return null;
  const fileIcon = (name) => {
    if (!name) return FileText;
    const ext = name.split(".").pop()?.toLowerCase();
    if (["mp4", "webm", "mov"].includes(ext)) return Video;
    if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) return Image;
    return FileText;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={onClose} data-testid="message-detail-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-stone-100 p-5 flex items-center justify-between rounded-t-2xl">
          <h2 className="text-lg font-bold text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>{t("admin.messageDetail")}</h2>
          <button onClick={onClose} className="p-2 hover:bg-stone-100 rounded-full transition-colors" data-testid="modal-close-btn"><X className="w-5 h-5 text-stone-500" /></button>
        </div>
        <div className="p-5 space-y-5">
          <div className="flex flex-wrap gap-3 items-center">
            <StatusBadge status={message.status} />
            {message.sent_at && <span className="text-xs text-stone-400">{t("admin.sentOn")} {new Date(message.sent_at).toLocaleString()}</span>}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-stone-50 rounded-xl p-4">
              <p className="text-xs text-stone-400 uppercase tracking-wide mb-1">{t("admin.senderLabel")}</p>
              <p className="font-semibold text-stone-800">{message.sender_name}</p>
              <p className="text-sm text-stone-500">{message.sender_email}</p>
            </div>
            <div className="bg-stone-50 rounded-xl p-4" data-testid="modal-recipients">
              <p className="text-xs text-stone-400 uppercase tracking-wide mb-1">
                {(message.recipients && message.recipients.length > 1)
                  ? `${t("admin.recipientLabel")} (${message.recipients.length})`
                  : t("admin.recipientLabel")}
              </p>
              {message.recipients && message.recipients.length > 0 ? (
                <div className="space-y-1.5">
                  {message.recipients.map((r, i) => (
                    <div key={i} data-testid={`modal-recipient-${i}`}>
                      <p className="font-semibold text-stone-800">{r.name || "—"}</p>
                      <p className="text-sm text-stone-500">{r.email}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  <p className="font-semibold text-stone-800">{message.recipient_name || "—"}</p>
                  <p className="text-sm text-stone-500">{message.recipient_email}</p>
                </>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-stone-50 rounded-xl p-4">
              <p className="text-xs text-stone-400 uppercase tracking-wide mb-1">{t("admin.deliveryDate")}</p>
              <p className="font-semibold text-stone-800">{message.delivery_date ? new Date(message.delivery_date).toLocaleString() : "—"}</p>
            </div>
            <div className="bg-stone-50 rounded-xl p-4">
              <p className="text-xs text-stone-400 uppercase tracking-wide mb-1">{t("admin.payment")}</p>
              <p className="font-semibold text-stone-800">
                {message.payment_status === "gifted"
                  ? <span className="inline-flex items-center gap-1 text-amber-700">🎁 {t("admin.giftedLabel") || "Offert (interne)"}</span>
                  : message.payment_session_id ? t("admin.paid") : t("admin.notPaid")}
              </p>
            </div>
          </div>
          {message.title && (<div><p className="text-xs text-stone-400 uppercase tracking-wide mb-1">{t("admin.titleCol")}</p><p className="font-semibold text-stone-800 text-lg">{message.title}</p></div>)}
          {message.backup_contact?.email && (
            <div className="bg-stone-50 rounded-xl p-4 border border-stone-200" data-testid="modal-backup-contact">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-4 h-4 text-stone-500" />
                <p className="text-xs text-stone-400 uppercase tracking-wide">{t("admin.backupContact")}</p>
                {message.backup_notified && <span className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 border border-red-200">{t("admin.notified")}</span>}
              </div>
              <p className="font-semibold text-stone-800">{message.backup_contact.name}</p>
              <p className="text-sm text-stone-500">{message.backup_contact.email}</p>
              {message.backup_contact.relationship && <p className="text-xs text-stone-400 mt-1">{t("admin.relationship")} {message.backup_contact.relationship}</p>}
            </div>
          )}
          <div>
            <p className="text-xs text-stone-400 uppercase tracking-wide mb-2">{t("admin.messageContent")}</p>
            <div className="bg-[#FDF0F4] rounded-xl p-4 text-stone-700 leading-relaxed whitespace-pre-wrap">
              {message.content || <span className="italic text-stone-400">{t("admin.noText")}</span>}
            </div>
          </div>
          {message.files && message.files.length > 0 && (
            <div>
              <p className="text-xs text-stone-400 uppercase tracking-wide mb-2">{t("admin.attachedFiles")} ({message.files.length})</p>
              <div className="space-y-2">
                {message.files.map((f, i) => {
                  const FIcon = fileIcon(f.file_name || f.original_name);
                  return (
                    <div key={i} className="flex items-center gap-3 bg-stone-50 rounded-lg p-3">
                      <FIcon className="w-5 h-5 text-[#E8175D]" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-stone-700 truncate">{f.original_name || f.file_name || `File ${i + 1}`}</p>
                        {f.file_size && <p className="text-xs text-stone-400">{(f.file_size / 1024 / 1024).toFixed(2)} Mo</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <div className="flex flex-wrap gap-2 pt-3 border-t border-stone-100">
            {message.status !== "sent" && (
              <>
                <button onClick={() => onAction("force-send", message.message_id)} className="flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-sm font-medium transition-colors" data-testid="modal-force-send-btn">
                  <Zap className="w-4 h-4" /> {t("admin.forceSend")}
                </button>
                <button onClick={() => onAction("pause", message.message_id)} className="flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-medium transition-colors" data-testid="modal-pause-btn">
                  {message.status === "paused" ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
                  {message.status === "paused" ? t("admin.resume") : t("admin.pause")}
                </button>
              </>
            )}
            <button onClick={() => onAction("delete", message.message_id)} className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-medium transition-colors ml-auto" data-testid="modal-delete-btn">
              <Trash2 className="w-4 h-4" /> {t("common.delete")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function EditDateModal({ message, onClose, onSave, t }) {
  const [date, setDate] = useState("");
  useEffect(() => {
    if (message?.delivery_date) setDate(new Date(message.delivery_date).toISOString().slice(0, 16));
  }, [message]);
  if (!message) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={onClose} data-testid="edit-date-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-bold text-stone-900 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>{t("admin.editDeliveryDate")}</h3>
        <p className="text-sm text-stone-500 mb-4">{t("admin.messageFor")} <strong>{message.recipient_name}</strong></p>
        <input type="datetime-local" value={date} onChange={(e) => setDate(e.target.value)} className="w-full border border-stone-200 rounded-lg px-4 py-3 text-stone-800 focus:outline-none focus:ring-2 focus:ring-[#E8175D]/30 focus:border-[#E8175D]" data-testid="edit-date-input" />
        <div className="flex gap-3 mt-5">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 border border-stone-200 rounded-lg text-stone-600 hover:bg-stone-50 transition-colors text-sm font-medium">{t("admin.cancel")}</button>
          <button onClick={() => onSave(message.message_id, date)} className="flex-1 px-4 py-2.5 bg-[#E8175D] hover:bg-[#c9134e] text-white rounded-lg transition-colors text-sm font-medium" data-testid="edit-date-save-btn">{t("common.save")}</button>
        </div>
      </div>
    </div>
  );
}

function ConfirmModal({ title, description, confirmLabel, confirmColor, onConfirm, onClose, t }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={onClose} data-testid="confirm-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-bold text-stone-900 mb-2">{title}</h3>
        <p className="text-sm text-stone-500 mb-5">{description}</p>
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 border border-stone-200 rounded-lg text-stone-600 hover:bg-stone-50 transition-colors text-sm font-medium">{t("admin.cancel")}</button>
          <button onClick={onConfirm} className={`flex-1 px-4 py-2.5 text-white rounded-lg transition-colors text-sm font-medium ${confirmColor}`} data-testid="confirm-action-btn">{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard({ user }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [messages, setMessages] = useState([]);
  const [recentUsers, setRecentUsers] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const [selectedMsg, setSelectedMsg] = useState(null);
  const [editDateMsg, setEditDateMsg] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const FILTER_LABELS = {
    all: t("admin.all"), paid: t("admin.scheduled"), sent: t("status.sent"),
    pending_payment: t("admin.pendingPayment"), paused: t("admin.paused"), delivery_failed: t("admin.failedDelivery"),
  };

  const formatDate = (dateStr) => dateStr ? new Date(dateStr).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "—";

  const fetchData = useCallback(async (statusFilter) => {
    try {
      const params = statusFilter && statusFilter !== "all" ? `?status=${statusFilter}` : "";
      const [statsRes, msgsRes, activityRes, chartRes] = await Promise.all([
        fetch(`${API}/api/admin/stats`, { credentials: "include" }),
        fetch(`${API}/api/admin/messages${params}`, { credentials: "include" }),
        fetch(`${API}/api/admin/recent-activity`, { credentials: "include" }),
        fetch(`${API}/api/admin/revenue-chart`, { credentials: "include" }),
      ]);
      if (!statsRes.ok) throw new Error(t("scheduled.accessDenied"));
      setStats(await statsRes.json());
      const msgsData = await msgsRes.json();
      setMessages(msgsData.messages || []);
      const actData = await activityRes.json();
      setRecentUsers(actData.recent_users || []);
      if (chartRes.ok) { const cData = await chartRes.json(); setChartData(cData.data || []); }
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [t]);

  useEffect(() => {
    if (user?.email !== "videogirl25@hotmail.com") { navigate("/"); return; }
    fetchData(filter);
  }, [user, navigate, filter, fetchData]);

  const refreshMessages = async () => {
    const params = filter && filter !== "all" ? `?status=${filter}` : "";
    const res = await fetch(`${API}/api/admin/messages${params}`, { credentials: "include" });
    const data = await res.json();
    setMessages(data.messages || []);
    const statsRes = await fetch(`${API}/api/admin/stats`, { credentials: "include" });
    setStats(await statsRes.json());
  };

  const viewMessage = async (messageId) => {
    const res = await fetch(`${API}/api/admin/messages/${messageId}`, { credentials: "include" });
    if (res.ok) setSelectedMsg(await res.json());
  };

  const handleAction = async (action, messageId) => {
    if (action === "delete") {
      setSelectedMsg(null);
      setConfirmAction({ action, messageId, title: t("admin.deleteConfirmTitle"), description: t("admin.deleteConfirmDesc"), confirmLabel: t("common.delete"), confirmColor: "bg-red-500 hover:bg-red-600" });
      return;
    }
    if (action === "force-send") {
      setSelectedMsg(null);
      setConfirmAction({ action, messageId, title: t("admin.forceSendConfirmTitle"), description: t("admin.forceSendConfirmDesc"), confirmLabel: t("admin.sendNow"), confirmColor: "bg-emerald-500 hover:bg-emerald-600" });
      return;
    }
    await executeAction(action, messageId);
  };

  const executeAction = async (action, messageId) => {
    setActionLoading(true);
    try {
      let res;
      if (action === "pause") res = await fetch(`${API}/api/admin/messages/${messageId}/pause`, { method: "PUT", credentials: "include" });
      else if (action === "force-send") res = await fetch(`${API}/api/admin/messages/${messageId}/force-send`, { method: "POST", credentials: "include" });
      else if (action === "delete") res = await fetch(`${API}/api/admin/messages/${messageId}`, { method: "DELETE", credentials: "include" });
      if (res?.ok) {
        const data = await res.json();
        toast.success(data.message || t("toast.actionDone"));
        setSelectedMsg(null);
        setConfirmAction(null);
        await refreshMessages();
      } else {
        const err = await res.json();
        toast.error(err.detail || t("common.error"));
      }
    } catch { toast.error(t("toast.networkError")); }
    finally { setActionLoading(false); }
  };

  const saveDeliveryDate = async (messageId, newDate) => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/messages/${messageId}/delivery-date`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ delivery_date: new Date(newDate).toISOString() }),
      });
      if (res.ok) { toast.success(t("toast.dateModified")); setEditDateMsg(null); await refreshMessages(); }
      else toast.error(t("toast.errorModify"));
    } catch { toast.error(t("toast.networkError")); }
    finally { setActionLoading(false); }
  };

  if (loading) return <div className="min-h-screen bg-[#FDF0F4] flex items-center justify-center"><div className="spinner" /></div>;
  if (error) return (
    <div className="min-h-screen bg-[#FDF0F4] flex items-center justify-center">
      <Card className="max-w-md"><CardContent className="p-8 text-center"><AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" /><p className="text-stone-700 font-medium">{error}</p></CardContent></Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FDF0F4]" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-stone-500 hover:text-[#E8175D] text-sm mb-2 transition-colors" data-testid="admin-back-btn">
              <ArrowLeft className="w-4 h-4" /> {t("admin.back")}
            </button>
            <h1 className="text-2xl font-bold text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>{t("admin.title")}</h1>
          </div>
          <Badge variant="outline" className="border-[#E8175D] text-[#E8175D]">Admin</Badge>
        </div>

        <button onClick={() => navigate("/admin/scheduled")} className="mb-8 flex items-center gap-3 w-full p-4 bg-white rounded-xl border border-stone-200 hover:border-[#E8175D] hover:shadow-sm transition-all group" data-testid="nav-scheduled-deliveries">
          <div className="p-2.5 rounded-lg bg-blue-500 group-hover:bg-[#E8175D] transition-colors"><CalendarDays className="w-5 h-5 text-white" /></div>
          <div className="text-left flex-1">
            <p className="font-semibold text-stone-800 text-sm">{t("admin.scheduledDeliveries")}</p>
            <p className="text-xs text-stone-400">{t("admin.scheduledDesc")}</p>
          </div>
          <ChevronDown className="w-5 h-5 text-stone-300 -rotate-90 group-hover:text-[#E8175D] transition-colors" />
        </button>

        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <StatCard icon={Users} title={t("admin.users")} value={stats.users.total} subtitle={`+${stats.users.new_this_month} ${t("admin.thisMonth")}`} color="bg-[#E8175D]" />
            <StatCard icon={Mail} title={t("admin.messages")} value={stats.messages.total} subtitle={`${stats.messages.sent} ${t("admin.sent")}`} color="bg-violet-500" />
            <StatCard icon={TrendingUp} title={t("admin.revenue")} value={`${stats.revenue.total.toFixed(0)}€`} subtitle={`${stats.revenue.this_month.toFixed(0)}€ ${t("admin.thisMonth")}`} color="bg-emerald-500" />
            <StatCard icon={CreditCard} title={t("admin.payments")} value={stats.revenue.total_payments} subtitle={`${stats.revenue.payments_this_month} ${t("admin.thisMonth")}`} color="bg-blue-500" />
          </div>
        )}

        {stats?.messages?.total_gifted_bizous !== undefined && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="admin-paid-vs-gifted">
            <StatCard
              icon={Lock}
              title={t("admin.paidBizous") || "Bizous payés"}
              value={stats.messages.total_paid_bizous}
              subtitle={t("admin.viaStripe") || "via Stripe"}
              color="bg-emerald-600"
            />
            <StatCard
              icon={Gift}
              title={t("admin.giftedBizous") || "Bizous cadeaux"}
              value={stats.messages.total_gifted_bizous}
              subtitle={t("admin.internalOnly") || "interne / promo"}
              color="bg-amber-500"
            />
          </div>
        )}

        {chartData.length > 0 && (
          <Card className="mb-8" data-testid="revenue-chart">
            <CardHeader className="pb-2"><CardTitle className="text-base font-semibold text-stone-800">{t("admin.monthlyRevenue")}</CardTitle></CardHeader>
            <CardContent className="pt-0">
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                  <defs><linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#E8175D" stopOpacity={0.25} /><stop offset="95%" stopColor="#E8175D" stopOpacity={0} /></linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0e8eb" />
                  <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#a8a29e" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 12, fill: "#a8a29e" }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}€`} />
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #f0e8eb", boxShadow: "0 4px 20px rgba(0,0,0,0.06)", fontSize: 13 }} formatter={(value, name) => [name === "revenue" ? `${value.toFixed(0)}€` : value, name === "revenue" ? t("admin.revenue") : t("admin.payments")]} labelStyle={{ fontWeight: 600, color: "#1c1917" }} />
                  <Area type="monotone" dataKey="revenue" stroke="#E8175D" strokeWidth={2.5} fill="url(#revGrad)" dot={{ r: 4, fill: "#E8175D", strokeWidth: 0 }} activeDot={{ r: 6, fill: "#E8175D", stroke: "#fff", strokeWidth: 2 }} />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        <Card className="mb-8">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <CardTitle className="text-base font-semibold text-stone-800">{t("admin.messageManagement")}</CardTitle>
              <div className="flex gap-2 flex-wrap" data-testid="message-filters">
                {FILTER_VALUES.map((val) => (
                  <button key={val} onClick={() => setFilter(val)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${filter === val ? "bg-[#E8175D] text-white shadow-sm" : "bg-white text-stone-600 border border-stone-200 hover:border-[#E8175D] hover:text-[#E8175D]"}`} data-testid={`filter-${val}`}>
                    {FILTER_LABELS[val]}
                  </button>
                ))}
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-messages-table">
                <thead>
                  <tr className="border-b border-stone-100 bg-stone-50/50">
                    <th className="text-left p-3 text-stone-500 font-medium">{t("admin.senderCol")}</th>
                    <th className="text-left p-3 text-stone-500 font-medium">{t("admin.recipientCol")}</th>
                    <th className="text-left p-3 text-stone-500 font-medium">{t("admin.titleCol")}</th>
                    <th className="text-left p-3 text-stone-500 font-medium">{t("admin.deliveryCol")}</th>
                    <th className="text-left p-3 text-stone-500 font-medium">{t("admin.statusCol")}</th>
                    <th className="text-center p-3 text-stone-500 font-medium">{t("admin.backupCol")}</th>
                    <th className="text-right p-3 text-stone-500 font-medium">{t("admin.actionsCol")}</th>
                  </tr>
                </thead>
                <tbody>
                  {messages.map((msg) => (
                    <tr key={msg.message_id} className="border-b border-stone-50 hover:bg-white/60 transition-colors">
                      <td className="p-3"><p className="font-medium text-stone-800 text-sm">{msg.sender_name}</p><p className="text-xs text-stone-400">{msg.sender_email}</p></td>
                      <td className="p-3" data-testid={`recipient-cell-${msg.message_id}`}>
                        {msg.recipients && msg.recipients.length > 0 ? (
                          <>
                            <p className="font-medium text-stone-800 text-sm flex items-center gap-1.5">
                              {msg.recipients[0].name}
                              {msg.recipients.length > 1 && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-semibold" title={msg.recipients.map(r => `${r.name} (${r.email})`).join(", ")}>
                                  +{msg.recipients.length - 1}
                                </span>
                              )}
                            </p>
                            <p className="text-xs text-stone-400">
                              {msg.recipients.length > 1
                                ? `${msg.recipients.length} destinataires`
                                : msg.recipients[0].email}
                            </p>
                          </>
                        ) : (
                          <>
                            <p className="font-medium text-stone-800 text-sm">{msg.recipient_name || "—"}</p>
                            <p className="text-xs text-stone-400">{msg.recipient_email}</p>
                          </>
                        )}
                      </td>
                      <td className="p-3 text-stone-700 max-w-[160px] truncate">{msg.title || "—"}</td>
                      <td className="p-3 text-stone-600 text-xs">{formatDate(msg.delivery_date)}</td>
                      <td className="p-3"><StatusBadge status={msg.status} /></td>
                      <td className="p-3 text-center">
                        {msg.backup_contact?.email ? (
                          <span title={`${msg.backup_contact.name} (${msg.backup_contact.email})`} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${msg.backup_notified ? "bg-red-100 text-red-600" : "bg-stone-100 text-stone-500"}`}>
                            <Shield className="w-3 h-3" />{msg.backup_notified ? t("admin.notified") : t("admin.yes")}
                          </span>
                        ) : <span className="text-stone-300 text-xs">—</span>}
                      </td>
                      <td className="p-3">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => viewMessage(msg.message_id)} className="p-1.5 hover:bg-stone-100 rounded-lg transition-colors" data-testid={`view-msg-${msg.message_id}`}><Eye className="w-4 h-4 text-stone-500" /></button>
                          {msg.status !== "sent" && (
                            <>
                              <button onClick={() => setEditDateMsg(msg)} className="p-1.5 hover:bg-stone-100 rounded-lg transition-colors" data-testid={`edit-date-${msg.message_id}`}><CalendarClock className="w-4 h-4 text-blue-500" /></button>
                              <button onClick={() => handleAction("pause", msg.message_id)} className="p-1.5 hover:bg-stone-100 rounded-lg transition-colors" data-testid={`pause-msg-${msg.message_id}`}>{msg.status === "paused" ? <Play className="w-4 h-4 text-green-500" /> : <Pause className="w-4 h-4 text-orange-500" />}</button>
                              <button onClick={() => handleAction("force-send", msg.message_id)} className="p-1.5 hover:bg-stone-100 rounded-lg transition-colors" data-testid={`force-send-${msg.message_id}`}><Zap className="w-4 h-4 text-emerald-500" /></button>
                            </>
                          )}
                          <button onClick={() => handleAction("delete", msg.message_id)} className="p-1.5 hover:bg-red-50 rounded-lg transition-colors" data-testid={`delete-msg-${msg.message_id}`}><Trash2 className="w-4 h-4 text-red-400" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {messages.length === 0 && (<tr><td colSpan={7} className="p-8 text-center text-stone-400">{t("admin.noMessages")}</td></tr>)}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-3 border-t border-stone-100 text-xs text-stone-400">
              {messages.length} message{messages.length !== 1 ? "s" : ""}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base font-semibold text-stone-800">{t("admin.recentUsers")}</CardTitle></CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-users-table">
                <thead>
                  <tr className="border-b border-stone-100 bg-stone-50/50">
                    <th className="text-left p-3 text-stone-500 font-medium">{t("admin.nameCol")}</th>
                    <th className="text-left p-3 text-stone-500 font-medium">{t("admin.emailCol")}</th>
                    <th className="text-left p-3 text-stone-500 font-medium">{t("admin.registeredCol")}</th>
                  </tr>
                </thead>
                <tbody>
                  {recentUsers.map((u) => (
                    <tr key={u.user_id} className="border-b border-stone-50 hover:bg-white/60">
                      <td className="p-3 font-medium text-stone-800">{u.name}</td>
                      <td className="p-3 text-stone-600">{u.email}</td>
                      <td className="p-3 text-stone-600">{formatDate(u.created_at)}</td>
                    </tr>
                  ))}
                  {recentUsers.length === 0 && (<tr><td colSpan={3} className="p-6 text-center text-stone-400">{t("admin.noUsers")}</td></tr>)}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      {selectedMsg && <MessageDetailModal message={selectedMsg} onClose={() => setSelectedMsg(null)} onAction={handleAction} t={t} />}
      {editDateMsg && <EditDateModal message={editDateMsg} onClose={() => setEditDateMsg(null)} onSave={saveDeliveryDate} t={t} />}
      {confirmAction && (
        <ConfirmModal title={confirmAction.title} description={confirmAction.description} confirmLabel={confirmAction.confirmLabel} confirmColor={confirmAction.confirmColor} onClose={() => setConfirmAction(null)} onConfirm={() => executeAction(confirmAction.action, confirmAction.messageId)} t={t} />
      )}
    </div>
  );
}
